import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import PublicShell from "../components/PublicShell";
import api from "../lib/api";
import { ShieldCheck, FileText } from "lucide-react";

const TITLE_BY_KEY = {
  terms: "Terms and Conditions",
  privacy: "Privacy Policy",
  refund_policy: "Refund and Cancellation Policy",
  cancellation_policy: "Cancellation Policy",
  cookie_policy: "Cookie Policy",
  billing_notice: "Billing Entity Notice",
  shipping_policy: "Digital Delivery / Shipping Policy",
};

/**
 * Sprint 4 — Public policy viewer (one page powers /terms /privacy
 * /refund-policy /cancellation-policy /cookies /billing-notice).
 */
export default function PolicyViewerPage({ policyKey }) {
  const params = useParams();
  const key = policyKey || params.policyKey;
  const [policy, setPolicy] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/policies/${key}`)
      .then((r) => setPolicy(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Could not load"));
  }, [key]);

  return (
    <PublicShell>
      <section className="bg-[#0A192F] text-white">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <p className="text-[10px] uppercase tracking-[0.36em] text-[#D4AF37] font-bold flex items-center gap-2">
            <ShieldCheck className="h-3 w-3" /> Diplomexa Policy
          </p>
          <h1 className="mt-3 text-4xl font-bold tracking-tight" data-testid="policy-title">
            {policy?.title || TITLE_BY_KEY[key] || "Policy"}
          </h1>
          {policy?.version && (
            <p className="mt-2 text-xs text-slate-400">
              Version {policy.version} · Effective {policy.effective_date?.slice(0, 10)}
            </p>
          )}
        </div>
      </section>
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 py-12">
          {err && <p className="text-rose-600">{err}</p>}
          {policy && (
            <article
              className="prose prose-slate max-w-none"
              data-testid="policy-body"
              dangerouslySetInnerHTML={{ __html: policy.body_html }}
            />
          )}

          {/* Entity-aware billing contacts — shown at the bottom of every policy */}
          <div className="mt-12 grid md:grid-cols-2 gap-4 text-xs text-slate-700" data-testid="policy-entity-contacts">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">India billing</p>
              <p className="mt-1 font-semibold text-[#0A192F]">Factskoot Eduwellness Private Limited</p>
              <p className="mt-1 text-slate-600">No. 43, WeWork Galaxy, Shanthala Nagar, Residency Road, Bengaluru 560025, Karnataka, India</p>
              <p className="mt-2 text-slate-600">
                <a className="text-[#0F4C81]" href="mailto:info@factskoot.com">info@factskoot.com</a> · +91-80-62177852 · WhatsApp +91 9844139333
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#0F4C81] font-bold">International billing</p>
              <p className="mt-1 font-semibold text-[#0A192F]">Destineze Educational Voyages FZCO</p>
              <p className="mt-1 text-slate-600">IFZA Business Park, Dubai Silicon Oasis, Dubai, United Arab Emirates</p>
              <p className="mt-2 text-slate-600">
                <a className="text-[#0F4C81]" href="mailto:info@destineze.com">info@destineze.com</a> · Mobile / WhatsApp +971 50 488 3656
              </p>
            </div>
          </div>

          <p className="mt-6 text-xs text-slate-500 flex items-start gap-2">
            <FileText className="h-3.5 w-3.5 mt-0.5" />
            This policy is provided as a draft pending final legal review. Please use the
            contact details above that match your billing geography.
          </p>
        </div>
      </section>
    </PublicShell>
  );
}
